import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, Plus, Trash2, CheckCircle, AlertCircle, TrendingUp, TrendingDown,
  Target, Activity, Mail, MessageSquare, Smartphone, Volume2, RefreshCw,
  Loader2, AlertTriangle, X
} from 'lucide-react';
import brokerAPI from '../../api/broker';

const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://trading-api-1075463475276.us-central1.run.app';

const PriceAlerts = ({ marketType = 'crypto' }) => {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [stats, setStats] = useState({ active: 0, triggered: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [newAlert, setNewAlert] = useState({
    symbol: '',
    condition: 'price_above',
    target_value: '',
    channels: ['in_app'],
    note: ''
  });

  const alertTypes = [
    { id: 'price_above', label: 'Price Above', icon: TrendingUp, color: '#10b981' },
    { id: 'price_below', label: 'Price Below', icon: TrendingDown, color: '#ef4444' },
  ];

  const channelOptions = [
    { id: 'in_app', label: 'In-App', icon: Bell },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sms', label: 'SMS', icon: MessageSquare },
    { id: 'push', label: 'Push', icon: Smartphone },
  ];

  const fetchAlerts = useCallback(async () => {
    try {
      setError(null);
      const [activeRes, triggeredRes, statsRes] = await Promise.all([
        brokerAPI.getAlerts('default', 'active'),
        brokerAPI.getAlerts('default', 'triggered'),
        brokerAPI.getAlertStats('default'),
      ]);
      setActiveAlerts(activeRes.alerts || []);
      setTriggeredAlerts(triggeredRes.alerts || []);
      setStats(statsRes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleAddAlert = async () => {
    if (!newAlert.symbol || !newAlert.target_value) return;
    setSubmitting(true);

    try {
      // Fetch current price
      let currentPrice = 0;
      try {
        const isCrypto = /USD$/.test(newAlert.symbol) || newAlert.symbol.includes('/');
        const assetType = isCrypto ? 'crypto' : 'stocks';
        const res = await fetch(`${API_BASE}/api/twelvedata/${assetType}/daily?symbol=${newAlert.symbol}&limit=1`);
        const data = await res.json();
        const rows = data.data || data.results || data;
        if (Array.isArray(rows) && rows.length > 0) {
          currentPrice = parseFloat(rows[0].close) || 0;
        }
      } catch {}

      await brokerAPI.createAlert({
        symbol: newAlert.symbol.toUpperCase(),
        condition: newAlert.condition,
        target_value: parseFloat(newAlert.target_value),
        channels: newAlert.channels,
        current_price: currentPrice,
        note: newAlert.note || undefined,
      });

      setNewAlert({ symbol: '', condition: 'price_above', target_value: '', channels: ['in_app'], note: '' });
      setShowAddAlert(false);
      await fetchAlerts();
    } catch (err) {
      alert(`Failed to create alert: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    setDeletingId(alertId);
    try {
      await brokerAPI.deleteAlert(alertId);
      await fetchAlerts();
    } catch (err) {
      alert(`Failed to delete alert: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleChannel = (ch) => {
    setNewAlert(prev => ({
      ...prev,
      channels: prev.channels.includes(ch)
        ? prev.channels.filter(c => c !== ch)
        : [...prev.channels, ch]
    }));
  };

  const getAlertProgress = (alert) => {
    if (!alert.current_price_at_creation || !alert.target_value) return 50;
    if (alert.condition === 'price_above') {
      return Math.min(100, (alert.current_price_at_creation / alert.target_value) * 100);
    } else if (alert.condition === 'price_below') {
      return Math.min(100, (alert.target_value / alert.current_price_at_creation) * 100);
    }
    return 50;
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)', minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={48} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: 0, color: 'white', fontSize: '32px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Bell size={36} color="#10b981" />
            Price Alerts
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#94a3b8', fontSize: '16px' }}>
            {stats.active} active • {stats.triggered} triggered • Persisted to BigQuery
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchAlerts} style={{ padding: '10px', background: '#1e293b', color: '#3b82f6', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <RefreshCw size={18} />
          </button>
          <button onClick={() => setShowAddAlert(!showAddAlert)} style={{
            padding: '12px 24px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold',
            display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
          }}>
            <Plus size={20} />
            Create Alert
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '16px', background: '#7f1d1d33', border: '1px solid #ef4444', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={20} color="#ef4444" />
          <span style={{ color: '#fca5a5' }}>{error}</span>
        </div>
      )}

      {/* Add Alert Form */}
      {showAddAlert && (
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '24px', borderRadius: '12px', marginBottom: '24px', border: '1px solid #334155' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: 'white', margin: 0 }}>Create New Alert</h3>
            <button onClick={() => setShowAddAlert(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Symbol</label>
              <input type="text" placeholder="e.g., BTCUSD, AAPL" value={newAlert.symbol}
                onChange={(e) => setNewAlert({ ...newAlert, symbol: e.target.value.toUpperCase() })}
                style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>

            <div>
              <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Condition</label>
              <select value={newAlert.condition} onChange={(e) => setNewAlert({ ...newAlert, condition: e.target.value })}
                style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: 'white', fontSize: '14px' }}>
                {alertTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Target Price</label>
              <input type="number" placeholder="Enter price" value={newAlert.target_value}
                onChange={(e) => setNewAlert({ ...newAlert, target_value: e.target.value })}
                style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>

            <div>
              <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Note (optional)</label>
              <input type="text" placeholder="Reason for alert..." value={newAlert.note}
                onChange={(e) => setNewAlert({ ...newAlert, note: e.target.value })}
                style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: 'white', fontSize: '14px' }} />
            </div>
          </div>

          {/* Notification Channels */}
          <div style={{ marginTop: '16px' }}>
            <label style={{ color: '#94a3b8', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Notifications</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {channelOptions.map(ch => (
                <button key={ch.id} onClick={() => toggleChannel(ch.id)} style={{
                  padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px',
                  background: newAlert.channels.includes(ch.id) ? '#3b82f622' : '#0f172a',
                  border: `1px solid ${newAlert.channels.includes(ch.id) ? '#3b82f6' : '#334155'}`,
                  borderRadius: '6px', cursor: 'pointer', color: newAlert.channels.includes(ch.id) ? '#60a5fa' : '#94a3b8', fontSize: '13px'
                }}>
                  <ch.icon size={14} /> {ch.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={handleAddAlert} disabled={submitting || !newAlert.symbol || !newAlert.target_value} style={{
            marginTop: '16px', padding: '10px 24px', background: '#10b981', color: 'white', border: 'none',
            borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold',
            opacity: submitting || !newAlert.symbol || !newAlert.target_value ? 0.5 : 1
          }}>
            {submitting ? 'Creating...' : 'Create Alert'}
          </button>
        </div>
      )}

      {/* Active Alerts */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '15px' }}>
          Active Alerts ({activeAlerts.length})
        </h2>

        {activeAlerts.length === 0 ? (
          <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '40px', borderRadius: '12px', border: '1px solid #334155', textAlign: 'center' }}>
            <Bell size={40} color="#334155" />
            <p style={{ color: '#94a3b8', marginTop: '12px' }}>No active alerts</p>
            <p style={{ color: '#64748b', fontSize: '13px' }}>Create an alert to get notified when prices hit your targets</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '15px' }}>
            {activeAlerts.map(alert => {
              const alertType = alertTypes.find(t => t.id === alert.condition);
              const progress = getAlertProgress(alert);

              return (
                <div key={alert.alert_id} style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '20px',
                  borderRadius: '12px', border: '1px solid #334155', position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {alertType && <alertType.icon size={24} color={alertType.color} />}
                      <div>
                        <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>{alert.symbol}</div>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>{alertType?.label}</div>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteAlert(alert.alert_id)} disabled={deletingId === alert.alert_id}
                      style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', padding: '6px', cursor: 'pointer', opacity: deletingId === alert.alert_id ? 0.5 : 1 }}>
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div style={{ background: '#0f172a', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>Target</span>
                      <span style={{ color: alertType?.color, fontSize: '14px', fontWeight: 'bold' }}>
                        ${alert.target_value?.toLocaleString()}
                      </span>
                    </div>
                    {alert.current_price_at_creation && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#94a3b8', fontSize: '12px' }}>Price at Creation</span>
                        <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>
                          ${alert.current_price_at_creation.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress */}
                  <div style={{ width: '100%', height: '6px', background: '#0f172a', borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: alertType?.color, transition: 'width 0.3s' }} />
                  </div>

                  {/* Channels & Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {alert.channels?.map(ch => {
                        const channel = channelOptions.find(c => c.id === ch);
                        return channel ? <channel.icon key={ch} size={14} color="#94a3b8" title={channel.label} /> : null;
                      })}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '11px' }}>
                      {alert.created_at ? new Date(alert.created_at).toLocaleDateString() : ''}
                    </div>
                  </div>
                  {alert.note && (
                    <div style={{ color: '#64748b', fontSize: '11px', marginTop: '8px', fontStyle: 'italic' }}>{alert.note}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Triggered Alerts */}
      {triggeredAlerts.length > 0 && (
        <div>
          <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '15px' }}>
            Recently Triggered ({triggeredAlerts.length})
          </h2>
          <div style={{ display: 'grid', gap: '12px' }}>
            {triggeredAlerts.map(alert => (
              <div key={alert.alert_id} style={{
                background: 'linear-gradient(135deg, #065f46 0%, #064e3b 100%)', padding: '15px 20px',
                borderRadius: '8px', border: '1px solid #10b981', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CheckCircle size={24} color="#10b981" />
                  <div>
                    <div style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                      {alert.symbol} - {alertTypes.find(t => t.id === alert.condition)?.label}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                      Target ${alert.target_value?.toLocaleString()} • Triggered at ${alert.triggered_price?.toLocaleString()} • {alert.triggered_at ? new Date(alert.triggered_at).toLocaleString() : ''}
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDeleteAlert(alert.alert_id)} style={{
                  background: 'transparent', color: '#94a3b8', border: '1px solid #334155',
                  borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px'
                }}>
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PriceAlerts;
